import {
  BAND_ACTIONS,
  EventBlocksType,
  EventUniqueBlocks,
  IEvent,
  IEventBlock,
  IEventPeopleBlock,
} from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { useMemo } from "react";
import { copy } from "shared/assets/copy";
import { FormBuilder, Message } from "shared/components";
import { Spacer } from "shared/layout";
import { IFieldType, IFormFields, IStatus } from "shared/types";
import { DateFormat, formatDateTime } from "shared/utils";

interface Props {
  sourceEvent?: IEvent | null;
  setStep: (step: 1 | 2 | 3) => void;
  sourceBlock: IEventBlock;
  blockType: EventBlocksType;
  handleModalClose: () => void;
  setIncludeRsvpUsers: (includeRsvpUsers: boolean) => void;
}

export const PushCopyBlockForm = ({
  sourceEvent,
  setStep,
  sourceBlock,
  blockType = EventUniqueBlocks.people,
  handleModalClose,
  setIncludeRsvpUsers,
}: Props) => {
  useClearActionMetaOnUnMount(BAND_ACTIONS.copyEventBlocks);

  const hasUsersWithRSVP =
    blockType === EventUniqueBlocks.people &&
    (sourceBlock as IEventPeopleBlock).users?.some(user => !!user.rsvp);

  const formFields: IFormFields = useMemo(() => {
    if (!hasUsersWithRSVP) return [];

    return [
      {
        type: IFieldType.radioGroup,
        name: "includeRsvpUsers",
        label: "",
        options: [
          {
            label: copy.copyBlock.rsvp.copyRSVP,
            value: "true",
            defaultChecked: true,
          },
          { label: copy.copyBlock.rsvp.noCopyRSVP, value: "false" },
        ],
        isInline: false,
        style: {
          gridColumn: "span 2",
          mobile: {
            gridColumn: "span 1",
          },
        },
      },
    ];
  }, [hasUsersWithRSVP]);

  const renderCopy = (c: string) => {
    return c
      .replaceAll("%blockTitle", String(sourceEvent?.title))
      .replaceAll(
        "%blockWarning",
        copy.copyBlock.blockWarnings[blockType as keyof typeof copy.copyBlock.blockWarnings] || ""
      )
      .replaceAll(
        "%sourceEventDate",
        formatDateTime(sourceEvent?.date!, {
          dateFormat: DateFormat.dmy,
          dateOnly: true,
        }) || ""
      )
      .replaceAll("%sourceEventTitle", sourceEvent?.title || "")
      .replaceAll(
        "%blockTypeCopy",
        copy.copyBlock.blockTypeCopies[
          blockType as keyof typeof copy.copyBlock.blockTypeCopies
        ].toLowerCase()
      );
  };

  return (
    <Spacer size="m">
      <Message status={IStatus.info}>
        {renderCopy(copy.pushCopyBlock.base)}
        {!hasUsersWithRSVP && "."}
        {hasUsersWithRSVP && renderCopy(copy.pushCopyBlock.copyRSVP)}
        {blockType === EventUniqueBlocks.people && (
          <>
            <br />
            <br />
            {renderCopy(copy.pushCopyBlock.thisAction)}
          </>
        )}
      </Message>
      <FormBuilder
        fields={formFields}
        onSubmit={(form: { includeRsvpUsers: string }) => {
          setIncludeRsvpUsers(form.includeRsvpUsers === "true");
          setStep(2);
        }}
        buttons={{
          primary: {
            children: "Continue",
          },
          secondary: {
            children: "Cancel",
            onClick: handleModalClose,
          },
        }}
        resetFormOnSubmit={true}
      />
    </Spacer>
  );
};
